<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

const props = defineProps({
  selectedContacts: { type: Array, required: true },
});
const emit = defineEmits(['clear-selection', 'refresh-contacts']);

const store = useStore();
const allLabels = useMapGetter('labels/getLabels');
const contacts = useMapGetter('contacts/getContacts');
const { t } = useI18n();

// Dialog ref for the delete confirmation
const deleteDialogRef = ref(null);

const isAssigning = ref(false);
const isUnassigning = ref(false);
const isDeleting = ref(false);

const showAssignDropdown = ref(false);
const showUnassignDropdown = ref(false);
const assignSearchQuery = ref('');
const unassignSearchQuery = ref('');

const selectedCount = computed(() => props.selectedContacts.length);

const generateRandomColor = () => {
  const colors = [
    '#FF6B6B',
    '#4ECDC4',
    '#45B7D1',
    '#FFA07A',
    '#98D8C8',
    '#F7DC6F',
    '#BB8FCE',
    '#85C1E2',
    '#F8B739',
    '#52B788',
  ];
  return colors[Math.floor(Math.random() * colors.length)];
};

const assignLabelMenuItems = computed(() => {
  let labelsList = allLabels.value || [];
  const searchQuery = assignSearchQuery.value
    ? assignSearchQuery.value.trim().toLowerCase()
    : '';

  if (searchQuery) {
    labelsList = labelsList.filter(label =>
      label.title.toLowerCase().includes(searchQuery)
    );
  }

  const labels = labelsList.map(label => ({
    label: label.title,
    value: label.id,
    thumbnail: { name: label.title, color: label.color },
    isSelected: false,
    action: 'assignLabel',
    isNewTag: false,
  }));

  // Check if search query doesn't match any existing label (exact match)
  if (searchQuery) {
    const matchesExisting = allLabels.value?.some(
      label => label.title.toLowerCase() === searchQuery
    );

    if (!matchesExisting) {
      // Add "Create tag" option at the top
      const createTagOption = {
        label: t('LABEL_MGMT.CREATE_TAG', {
          name: assignSearchQuery.value.trim(),
        }),
        value: 'create-new',
        thumbnail: {
          name: assignSearchQuery.value.trim(),
          color: generateRandomColor(),
        },
        isSelected: false,
        action: 'createLabel',
        isNewTag: true,
        newTagName: assignSearchQuery.value.trim(),
      };
      return [createTagOption, ...labels];
    }
  }

  return labels;
});

const unassignLabelMenuItems = computed(() => {
  let labelsList = allLabels.value || [];
  const searchQuery = unassignSearchQuery.value
    ? unassignSearchQuery.value.trim().toLowerCase()
    : '';

  // Filter by search query
  if (searchQuery) {
    labelsList = labelsList.filter(label =>
      label.title.toLowerCase().includes(searchQuery)
    );
  }

  // Filter by applicability (only show labels present on selected contacts)
  if (contacts.value && props.selectedContacts.length) {
    const selectedContactObjects = contacts.value.filter(c =>
      props.selectedContacts.includes(c.id)
    );
    const applicableLabelTitles = new Set();

    selectedContactObjects.forEach(contact => {
      if (contact.labels) {
        contact.labels.forEach(l => {
          // Handle both string (title) and object structure
          const title = typeof l === 'string' ? l : l.title;
          if (title) applicableLabelTitles.add(title);
        });
      }
    });

    labelsList = labelsList.filter(label =>
      applicableLabelTitles.has(label.title)
    );
  }

  return labelsList.map(label => ({
    label: label.title,
    value: label.id,
    thumbnail: { name: label.title, color: label.color },
    isSelected: false,
    action: 'unassignLabel',
    isNewTag: false,
  }));
});

const handleAssignLabelAction = async ({ action, value, newTagName }) => {
  if (isAssigning.value) return;

  isAssigning.value = true;
  try {
    let tagTitle;

    if (action === 'createLabel') {
      // Create and assign new label
      tagTitle = newTagName;
    } else {
      // Find existing label
      const selectedLabel = allLabels.value.find(label => label.id === value);
      if (!selectedLabel) return;
      tagTitle = selectedLabel.title;
    }

    await store.dispatch('contacts/bulkAssignLabels', {
      contactIds: props.selectedContacts,
      labels: [tagTitle],
    });

    // Refresh all labels to get the newly created one
    if (action === 'createLabel') {
      await store.dispatch('labels/get');
    }

    useAlert(
      `Successfully assigned tag "${tagTitle}" to ${selectedCount.value} contact(s)`
    );

    showAssignDropdown.value = false;
    assignSearchQuery.value = '';
    emit('refresh-contacts');
  } catch (error) {
    console.error('[BulkActions] Failed to assign label:', error);
    useAlert('Failed to assign label. Please try again.');
  } finally {
    isAssigning.value = false;
  }
};

const handleUnassignLabelAction = async ({ value }) => {
  if (isUnassigning.value) return;

  isUnassigning.value = true;
  try {
    const selectedLabel = allLabels.value.find(label => label.id === value);
    if (!selectedLabel) return;

    await store.dispatch('contacts/bulkUnassignLabels', {
      contactIds: props.selectedContacts,
      labels: [selectedLabel.title],
    });

    useAlert(
      `Successfully removed tag "${selectedLabel.title}" from ${selectedCount.value} contact(s)`
    );

    showUnassignDropdown.value = false;
    unassignSearchQuery.value = '';
    emit('refresh-contacts');
  } catch (error) {
    console.error('[BulkActions] Failed to remove label:', error);
    useAlert('Failed to remove label. Please try again.');
  } finally {
    isUnassigning.value = false;
  }
};

const closeAssignDropdown = () => {
  showAssignDropdown.value = false;
  assignSearchQuery.value = '';
};

const closeUnassignDropdown = () => {
  showUnassignDropdown.value = false;
  unassignSearchQuery.value = '';
};

const handleDeleteClick = () => {
  deleteDialogRef.value?.open();
};

const confirmDelete = async () => {
  if (isDeleting.value) return;

  console.log('Starting bulk delete for contacts:', props.selectedContacts);
  isDeleting.value = true;

  try {
    const response = await store.dispatch('contacts/bulkDelete', {
      contactIds: props.selectedContacts,
    });

    console.log('Bulk delete successful:', response);
    useAlert(
      `Successfully deleted ${selectedCount.value} contact${selectedCount.value > 1 ? 's' : ''}`
    );

    // Clear selection first
    emit('clear-selection');

    // Refresh to fetch fresh data from server
    // No mutations are committed during bulkDelete, so no need to wait
    emit('refresh-contacts');
  } catch (error) {
    console.error('Bulk delete error:', error);
    useAlert(
      `Failed to delete contacts: ${error.message || 'Please try again.'}`
    );
  } finally {
    isDeleting.value = false;
  }
};

const cancelDelete = () => {
  // Don't call dialogRef.value?.close() as it causes infinite recursion
  // The Dialog component handles its own closing
  emit('clear-selection'); // Just clear selection if needed
};

// Handle keyboard events
const handleKeydown = event => {
  if (event.key === 'Escape') {
    deleteDialogRef.value?.close();
  }
};

onMounted(() => {
  document.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown);
});
</script>

<template>
  <div class="flex items-center gap-2">
    <!-- Clear Selection Button -->
    <Button
      variant="ghost"
      size="sm"
      class="text-slate-600 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400"
      @click="emit('clear-selection')"
    >
      Clear
    </Button>

    <div class="w-px h-4 bg-slate-300 dark:bg-slate-600" />

    <div class="flex items-center gap-2">
      <!-- Assign Tag Button -->
      <div class="relative">
        <button
          class="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 border border-slate-300 dark:border-slate-700 transition-colors"
          :disabled="isAssigning || isUnassigning || isDeleting"
          @click.stop="showAssignDropdown = !showAssignDropdown"
        >
          <span class="i-lucide-tag w-4 h-4" />
          <span>+ tag</span>
        </button>

        <div
          v-if="showAssignDropdown"
          v-on-clickaway="closeAssignDropdown"
          class="absolute top-full left-0 mt-1 z-50"
        >
          <DropdownMenu
            :menu-items="assignLabelMenuItems"
            :show-search="true"
            search-placeholder="Search or create tag..."
            @action="handleAssignLabelAction"
            @search="assignSearchQuery = $event"
          />
        </div>
      </div>

      <!-- Unassign Tag Button -->
      <div class="relative">
        <button
          class="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 border border-slate-300 dark:border-slate-700 transition-colors"
          :disabled="isAssigning || isUnassigning || isDeleting"
          @click.stop="showUnassignDropdown = !showUnassignDropdown"
        >
          <span class="i-lucide-tag w-4 h-4" />
          <span>- tag</span>
        </button>

        <div
          v-if="showUnassignDropdown"
          v-on-clickaway="closeUnassignDropdown"
          class="absolute top-full left-0 mt-1 z-50"
        >
          <DropdownMenu
            :menu-items="unassignLabelMenuItems"
            :show-search="true"
            search-placeholder="Search tags..."
            @action="handleUnassignLabelAction"
            @search="unassignSearchQuery = $event"
          />
        </div>
      </div>

      <!-- Delete Button -->
      <div class="relative">
        <button
          class="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 border border-red-200 dark:border-red-800 transition-colors"
          :disabled="isAssigning || isUnassigning || isDeleting"
          @click="handleDeleteClick"
        >
          <span class="i-lucide-trash-2 w-4 h-4" />
          <span>Delete now</span>
        </button>
      </div>
    </div>
  </div>

  <!-- Delete Confirmation Modal -->
  <Dialog
    ref="deleteDialogRef"
    type="alert"
    title="Confirm Deletion"
    :description="`Are you sure you want to delete ${selectedCount === 1 ? 'this contact' : 'these contacts'}?`"
    confirm-button-label="Yes, Delete"
    cancel-button-label="Cancel"
    :is-loading="isDeleting"
    @confirm="confirmDelete"
  />
</template>
